<template>
  <div :class="['print_resume', { loaded : loaded }]">
    <div class="main">
      <div class="loading">
        <inline-spinner :ticks="21" label="" />
      </div>

      <div class="body">
        <div class="error">{{ errorMsg }}</div>
        <div class="resume" v-if="resumeModel">
          <section class="profile print_whole">
            <div class="name">{{ resumeModel.fullName() }}</div>
            <div class="contact">
              <div class="address">{{ resumeModel.fullAddress() }}</div>
              <div class="email">{{ resumeModel.primaryEmail() }}</div>
              <div class="phone">{{ resumeModel.primaryPhone() }}</div>
              <div class="url"
                   v-for="url in resumeModel.normalUrls()"
                   :key="url.value">{{ url.baseUrl }}</div>
            </div>
          </section>
          <section class="objective print_whole">
            <h1>Objective</h1>
            <div class="section_content">
              <p v-for="(fragment, i) in objectiveMarkup"
                 :key="i"
                 class="md"
                 v-html="fragment"></p>
            </div>
          </section>

          <section class="education print_whole">
            <h1>Education</h1>
            <div class="section_content">
              <education v-for="(item, i) in resumeModel.education"
                         :key="i"
                         :model="item"></education>
            </div>
          </section>

          <section class="skills print_whole">
            <h1>Skills</h1>
            <div class="section_content">
              <div class="skill_columns">
                <div class="skill_column" v-for="n in skillColCt" :key="n">
                  <skill-type v-for="(item, i) in skillTypeBuckets[n - 1]"
                              :key="i"
                              :model="item"></skill-type>
                </div>
              </div>
            </div>
          </section>

          <section class="projects">
            <h1>Independent Projects</h1>
            <div class="section_content">
              <indy-project v-for="(item, i) in resumeModel.projects" :key="i"
                            :model="item"></indy-project>
            </div>
          </section>

          <section class="experience">
            <h1>Work Experience</h1>
            <div class="section_content">
              <experience v-for="(item, i) in resumeModel.experience" :key="i"
                          :model="item"></experience>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios';
import InlineSpinner from '@/components/decoration/inline-spinner';
import promiseUtil from '@/util/promise';
import Resume from '@/model/resume';
import Education from '@/components/print-resume/education';
import Experience from '@/components/print-resume/experience';
import IndyProject from '@/components/print-resume/project';
import SkillType from '@/components/print-resume/skill-type';
import MarkdownMixin from '@/mixins/markdown';

export default {
    name       : "PrintResume",
    components : {
        Education,
        Experience,
        IndyProject,
        InlineSpinner,
        SkillType
    },
    mixins     : [ MarkdownMixin ],
    props      : {
        accessCode : {
            type : String
        },
        autoPrint : {
            type    : Boolean,
            default : false
        }
    },
    data() {
        return {
            resumeModel : null,
            loaded      : false,
            errorMsg    : '',
            skillColCt  : 3
        };
    },
    mounted() {
        this.submit();
    },
    computed : {
        objectiveMarkup() {
            const model = this.resumeModel;

            if (model) {
                return model.data.objective.map(text => {
                    return this.markdown.renderInline(text.trim());
                });
            }

            return [];
        },
        skillTypeBuckets() {
            const ct      = this.skillColCt;
            const buckets = Array(ct).fill().map(() => []);
            const fills   = Array(ct).fill().map(() => 0);

            if (this.resumeModel) {
                this.resumeModel.skillTypes.forEach((model) => {
                    let idx = 0;
                    let max = fills[idx];

                    fills.forEach((value, i) => {
                        if (value < max) {
                            max = value;
                            idx = i;
                        }
                    });

                    buckets[idx].push(model);
                    fills[idx] += model.findSkillList().length;
                });
            }

            return buckets;
        }
    },
    methods : {
        setError(msg) {
            this.errorMsg = msg;
        },
        clearError() {
            this.setError('');
        },
        submit() {
            this.clearError();

            this.resumeModel = null;

            const p1 = axios({
                method       : 'get',
                timeout      : 5000,
                url          : '/ws/resume/',
                responseType : 'json',
                params       : { access : this.accessCode },
                headers      : {
                    'Content-Type' : "application/json"
                }
            });
            const p2 = promiseUtil.wait(1000);

            Promise.all([p1, p2]).then(
                response => { this.loadResponse(response); },
                error => { this.errorResponse(error); }
            ).finally(() => { this.loaded = true; });
        },
        loadResponse(response) {
            this.resumeModel = new Resume(response[0].data);
            setTimeout(() => { this.$emit('loaded'); }, 2000);
        },
        errorResponse(error) {
            let msg = "Error fetching resume data";
            if (error.response && error.response.data) {
                msg = error.response.data.message;
            }
            this.setError(msg);
        }
    }
};

</script>

<style scoped>

.print_resume {
    text-align:center;
    margin:0px auto;
    width:6.5in;
    background-color:var(--core-white);
    color:var(--main-gray);
}

.print_whole {
    break-inside:avoid;
}

.main {
    position:relative;
}

.loading {
    position:absolute;
    top:0px;
    left:0px;
    width:100%;
    height:50px;
    opacity:1;
    transition:opacity 2000ms, top 2000ms;
    z-index:2;
}

.body {
    position:relative;
    top:200px;
    opacity:0;
    transition:opacity 2000ms, top 2000ms;
    z-index:1;
    padding-top:20px;
}

.loaded .loading {
    opacity:0;
    top:200px;
}

.loaded .body {
    top:0px;
    opacity:1;
}

.error {
    background-image:-webkit-linear-gradient(to right, #FF0000, #990000);
    background-image:-moz-linear-gradient(to right, #FF0000, #990000);
    background-image:-ms-linear-gradient(to right, #FF0000, #990000);
    background-image:linear-gradient(to right, #FF0000, #990000);
    font-size:32px;
    color:#FFFFFF;
    padding:25px 0px;
    margin:1px 0px 0px;
}

.error:empty {
    display:none;
}

section {
}

h1 {
    font-size:16px;
    font-weight:normal;
    text-transform:uppercase;
    letter-spacing:0.35em;
    color:var(--resume-gray);
    margin:0px 0px 20px;
}

section .section_content > p {
    font-size:12px;
    color:var(--main-gray);
    text-align:left;
}


section.profile {
    border-top:1px solid var(--main-orange);
    border-bottom:1px solid var(--main-orange);
    padding:20px 0px;
    margin-bottom:20px;
}

section.profile > .name {
    font-size:24px;
    color:var(--resume-gray);
    letter-spacing:0.35em;
    text-transform:uppercase;
}

section.profile > .contact {
    margin-top:20px;
}

section.profile > .contact > * {
    font-size:12px;
    line-height:26px;
    letter-spacing:0.25em;
}

section.profile > .contact > .address,
section.profile > .contact > .email,
section.profile > .contact > .phone {
    text-transform:uppercase;
}

section.objective {
    margin:100px 0px;
}

section.education {
    margin:100px 0px;
}

section.skills {
    break-inside:avoid;
}

.skill_columns {
    display:flex;
    flex-wrap:nowrap;
    justify-content:space-around;
    align-items:flex-start;
}

/*
section.experience {
    break-before:page;
}
 */

@media only screen and (max-width:500px) {
    .error {
        font-size:24px;
    }
}

@media print {
    section.profile {
        border-top:1px solid var(--resume-light-gray);
        border-bottom:1px solid var(--resume-light-gray);
    }
    .error {
        display:none;
    }
}

</style>
